import React, { useState, useEffect, memo } from "react";
import moment from "moment";
import { SITE_ID } from "../../assets/meta/meta";

type CouponDiscountType = "price" | "percentage";

type ReservationCouponData = {
  title: string;
  expire_date?: string | null;
  use_start_date?: string | null;
  use_end_date?: string | null;
  discount_type: CouponDiscountType;
  discount_amount: number;
  discount_rate: number;
  min_purchase: number;
  max_discount: number;
  sites?: Array<string | number> | null;
  bound_type: string | number;
  use_holiday?: boolean;
  use_mon?: boolean;
  use_tue?: boolean;
  use_wed?: boolean;
  use_thu?: boolean;
  use_fri?: boolean;
  use_sat?: boolean;
  use_sun?: boolean;
  packages?: Array<{ id?: string | number } | string | number> | null;
};

type ReservationCouponItem = {
  id: string | number;
  state: string;
  coupon: ReservationCouponData;
};

type ReservationSeat = {
  reserve_date?: string;
  start_date?: string;
  end_date?: string;
  spot_id: string | number;
  price: number;
  product_id: string | number;
};

type SelectedReservationCoupon = {
  coupon_id: string | number;
  product_id: string | number;
  spot_id: string | number;
  coupon_title?: string;
};

type ToggleReservationDiscountCouponSelection = (
  productId: ReservationSeat["product_id"],
  couponId: ReservationCouponItem["id"],
  spotId: ReservationSeat["spot_id"],
  discountAmount: number,
  discountType: CouponDiscountType,
  minPurchase: number,
  discountRate: number,
  maxDiscount: number,
  couponTitle?: string
) => void;

interface CouponReservationItemProps {
  item: ReservationCouponItem;
  index: number;
  pageIndex?: number;
  seats: ReservationSeat;
  selectCoupon: SelectedReservationCoupon[];
  toggleReservationDiscountCouponSelection: ToggleReservationDiscountCouponSelection;
  boundType: string | number;
  holidays?: string[];
}

const CouponReservationItem = memo((props: CouponReservationItemProps) => {
  const {
    item,
    index,
    pageIndex,
    seats,
    selectCoupon,
    toggleReservationDiscountCouponSelection,
    boundType,
    holidays,
  } = props;

  const [disabled, setDisabled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [validationStates, setValidationStates] = useState({
    isBlocked: false,
    isExpired: false,
    isDateValid: false,
    isUsedSite: false,
    isUsed: false,
    isPriceValid: false,
    isDateAvailable: true,
    isPackageIncluded: false
  });

  useEffect(() => {
    const today = moment(moment().format("YYYY-MM-DD"));
    const reserveDate = seats.reserve_date || seats.start_date || "";
    const target_date = moment(reserveDate, "YYYY-MM-DD");
    const expireDate = item.coupon.expire_date ? moment(String(item.coupon.expire_date).slice(0, 10), "YYYY-MM-DD") : null;
    const useStartDate = item.coupon.use_start_date ? moment(item.coupon.use_start_date).startOf("day") : null;
    const useEndDate = item.coupon.use_end_date ? moment(item.coupon.use_end_date).endOf("day") : null;
    const couponSites = item.coupon.sites || [];
    const couponPackages = item.coupon.packages || [];

    // Calculate all validation states
    const isBlocked = !!selectCoupon.find((coupon) => coupon.coupon_id === item.id && coupon.spot_id !== seats.spot_id);
    const isExpired = expireDate ? today > expireDate : false;
    const isDateValid = useStartDate && useEndDate
      ? moment(reserveDate).isBetween(useStartDate, useEndDate, null, "[]")
      : true;
    const isUsedSite = couponSites.length ? couponSites.some((site) => site === SITE_ID) : true;
    const isUsed = item.state !== "사용안함";
    const isPriceValid = Number(item.coupon.min_purchase) <= Number(seats.price);
    const isPackageIncluded = couponPackages.length ? couponPackages.some((packageItem) => {
      if (typeof packageItem === "object" && packageItem !== null) {
        return packageItem.id === seats.product_id;
      }
      return packageItem === seats.product_id;
    }) : true;

    // Check day availability
    let isDateAvailable = true;
    let isTodayHoliday = holidays?.find((holiday) => holiday === reserveDate);

    if (isTodayHoliday && item.coupon.use_holiday) isDateAvailable = true;
    else if (item.coupon.use_mon === false && target_date.day() === 1) isDateAvailable = false;
    else if (item.coupon.use_tue === false && target_date.day() === 2) isDateAvailable = false;
    else if (item.coupon.use_wed === false && target_date.day() === 3) isDateAvailable = false;
    else if (item.coupon.use_thu === false && target_date.day() === 4) isDateAvailable = false;
    else if (item.coupon.use_fri === false && target_date.day() === 5) isDateAvailable = false;
    else if (item.coupon.use_sat === false && target_date.day() === 6) isDateAvailable = false;
    else if (item.coupon.use_sun === false && target_date.day() === 0) isDateAvailable = false;

    // Update validation states
    setValidationStates({
      isBlocked,
      isExpired,
      isDateValid,
      isUsedSite,
      isUsed,
      isPriceValid,
      isDateAvailable,
      isPackageIncluded
    });

    // Determine if coupon should be disabled
    const newDisabled = isExpired || isUsed || item.coupon.bound_type !== boundType || isBlocked ||
      !isDateValid || !isUsedSite || !isPriceValid || !isDateAvailable || !isPackageIncluded;
    setDisabled(newDisabled);

    // Determine if coupon should be hidden
    const newHidden = isExpired || isUsed || item.coupon.bound_type !== boundType;
    setHidden(newHidden);

  }, [item, seats, selectCoupon, boundType, holidays]);

  if (hidden) return null;

  const selected = selectCoupon.length > 0
    ? !!selectCoupon.find((coupons) => coupons.coupon_id === item.id && coupons.product_id === seats.product_id && coupons.spot_id === seats.spot_id)
    : false;
  const isPercentage = item.coupon.discount_type === "percentage";
  const formatWon = (value: number) => {
    const num = Number(value || 0);
    if (num >= 10000 && num % 10000 === 0) return `${(num / 10000).toLocaleString()}만원`;
    return `${num.toLocaleString()}원`;
  };
  // Usage period line: prefer use_end_date, fall back to expire_date
  const periodSource = item.coupon.use_end_date || item.coupon.expire_date || "";
  const periodLabel = periodSource
    ? `~${moment(String(periodSource).slice(0, 10)).format("YYYY.MM.DD")} 까지`
    : "상시 사용 가능";
  // Condition line: "100원 이상 결제 시 (최대 1만원)"
  const conditionLabel = `${Number(item.coupon.min_purchase || 0).toLocaleString()}원 이상 결제 시`
    + (isPercentage && Number(item.coupon.max_discount || 0) > 0
      ? ` (최대 ${formatWon(Number(item.coupon.max_discount || 0))})`
      : "");
  // Disabled reason: if the reservation date is before the coupon's start date,
  // show "X부터 사용 가능" so the user understands why it's blocked.
  const reserveDate = seats.reserve_date || seats.start_date || "";
  const useStartMoment = item.coupon.use_start_date
    ? moment(item.coupon.use_start_date).startOf("day")
    : null;
  const notStartedYet = !!useStartMoment && !!reserveDate
    && moment(reserveDate, "YYYY-MM-DD").isBefore(useStartMoment);
  const disabledMessage = notStartedYet
    ? `${moment(String(item.coupon.use_start_date).slice(0, 10)).format("YYYY.MM.DD")}부터 사용 가능`
    : "사용할 수 없는 쿠폰입니다.";
  const selectCouponItem = () => {
    if (disabled || selected) return;

    toggleReservationDiscountCouponSelection(
      seats.product_id,
      item.id,
      seats.spot_id,
      item.coupon.discount_amount,
      item.coupon.discount_type,
      item.coupon.min_purchase,
      item.coupon.discount_rate,
      item.coupon.max_discount,
      item.coupon.title
    );
  };
  const cancelCouponItem = () => {
    if (disabled || !selected) return;

    toggleReservationDiscountCouponSelection(
      seats.product_id,
      item.id,
      seats.spot_id,
      item.coupon.discount_amount,
      item.coupon.discount_type,
      item.coupon.min_purchase,
      item.coupon.discount_rate,
      item.coupon.max_discount,
      item.coupon.title
    );
  };

  // 클릭 토글: 선택돼 있으면 해제, 아니면 선택
  const handleToggle = () => {
    if (disabled) return;
    if (selected) cancelCouponItem();
    else selectCouponItem();
  };

  return (
    <li
      onClick={handleToggle}
      className={[
        "relative flex rounded-xl overflow-hidden border transition-[transform,border-color,box-shadow] duration-300 ease-[cubic-bezier(0.2,0,0,1)] select-none",
        disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer active:scale-[0.96]",
        selected
          ? "border-[#e16036] shadow-[0_4px_14px_rgba(225,96,54,0.18)]"
          : "border-gray-200 shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:border-[#f0c4b0]",
      ].join(" ")}
    >
      {/* 티켓 노치 (점선 위치 상·하단 반원 홈) */}
      <span className="absolute left-20 top-0 z-10 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gray-100" />
      <span className="absolute left-20 bottom-0 z-10 h-3 w-3 -translate-x-1/2 translate-y-1/2 rounded-full bg-gray-100" />

      {/* 왼쪽: 할인 금액/율 강조 (선택 시 브랜드색 배경 + 흰 글씨) */}
      <div className={[
        "w-20 shrink-0 flex flex-col items-center justify-center px-2 py-4 transition-colors duration-300 ease-[cubic-bezier(0.2,0,0,1)]",
        selected ? "bg-[#e16036]" : "bg-[#FFF0EA]",
      ].join(" ")}>
        {isPercentage ? (
          <>
            <span className={`text-2xl font-bold leading-tight tabular-nums transition-colors duration-300 ease-[cubic-bezier(0.2,0,0,1)] ${selected ? "text-white" : "text-[#e16036]"}`}>
              {Number(item.coupon.discount_rate || 0)}%
            </span>
            <span className={`text-[11px] font-semibold transition-colors duration-300 ease-[cubic-bezier(0.2,0,0,1)] ${selected ? "text-orange-100" : "text-[#e16036]"}`}>할인</span>
          </>
        ) : (
          <>
            <span className={`text-lg font-bold leading-tight tabular-nums transition-colors duration-300 ease-[cubic-bezier(0.2,0,0,1)] ${selected ? "text-white" : "text-[#e16036]"}`}>
              {Number(item.coupon.discount_amount || 0).toLocaleString()}
            </span>
            <span className={`text-[11px] font-semibold transition-colors duration-300 ease-[cubic-bezier(0.2,0,0,1)] ${selected ? "text-orange-100" : "text-[#e16036]"}`}>원 할인</span>
          </>
        )}
      </div>

      {/* 점선 구분 */}
      <div className="border-l-2 border-dashed border-gray-200 self-stretch" />

      {/* 오른쪽: 쿠폰 정보 */}
      <div className="flex-1 px-4 py-3.5 flex items-center justify-between gap-2 min-w-0">
        <div className="min-w-0">
          <p className="m-0 mb-1 line-clamp-2 text-sm font-bold text-gray-900" style={{ fontFamily: "Gmarket-B" }}>
            {item.coupon.title}
          </p>
          {disabled ? (
            <p className="m-0 text-xs font-medium text-red-600">{disabledMessage}</p>
          ) : (
            <>
              <p className="m-0 mb-0.5 text-xs font-semibold text-[#e16036]">{periodLabel}</p>
              <p className="m-0 text-xs text-gray-700">{conditionLabel}</p>
            </>
          )}
        </div>

        {!disabled && (
          <div className={[
            "shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors duration-300 ease-[cubic-bezier(0.2,0,0,1)]",
            selected ? "bg-[#e16036] border-[#e16036]" : "border-gray-300",
          ].join(" ")}>
            <svg
              className={[
                "w-3 h-3 text-white transition-[opacity,scale,filter] duration-300 ease-[cubic-bezier(0.2,0,0,1)]",
                selected ? "opacity-100 scale-100 blur-0 delay-75" : "opacity-0 scale-[0.25] blur-[4px]",
              ].join(" ")}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        )}
      </div>
    </li>
  );
});

export default CouponReservationItem;
